import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getDashboardSummary } from '../../services/DashboardService';
import { getAiUsage } from '../../services/SettingsService';
import { getAutomationsCounts } from '../../services/AutomationsService';
import { getCurrentEnvironment } from '../../utils/CurrentEnvironmentStorage';
import { getCurrentMarket, subscribeToCurrentMarket } from '../../utils/MarketStorage';

function StatCard({ label, value, sub, to, colorClass }) {
    const content = (
        <div className='card border-0 shadow p-3 h-100'>
            <div className='d-flex align-items-center justify-content-between'>
                <div>
                    <p className='text-muted mb-1' style={{ fontSize: 12 }}>{label}</p>
                    <h4 className={`mb-0 fw-bold ${colorClass || ''}`}>{value}</h4>
                    {sub && <small className='text-muted'>{sub}</small>}
                </div>
            </div>
        </div>
    );

    if (to) return <Link to={to} className='text-decoration-none'>{content}</Link>;
    return content;
}

function formatPnl(value) {
    if (value === null || value === undefined) return '—';
    // 🚨 O sinal do NEGATIVO era perdido: só o '+' era prefixado e o Math.abs comia o menos, então
    // uma perda de 25,89 saía como "$25.89" — a única pista de que era prejuízo era a cor
    // vermelha, e o percentual ao lado (que usa o sinal nativo do toFixed) mostrava "-25.72%",
    // contradizendo o valor. Número de dinheiro tem que carregar o próprio sinal.
    const sign = value >= 0 ? '+' : '-';
    return `${sign}$${Math.abs(value).toFixed(2)}`;
}
// ROI do dia (P&L ÷ capital investido em compras). null = sem compras → não exibe.
function pctStr(v) {
    if (v === null || v === undefined) return '';
    const n = parseFloat(v);
    return `${n >= 0 ? '+' : ''}${n.toFixed(2)}%`;
}

function DashboardSummary({ aiProvider, aiMonthlyTokenLimit }) {
    const [summary, setSummary] = useState(null);
    const [aiUsage, setAiUsage] = useState(null);
    const [autoCounts, setAutoCounts] = useState(null);
    const [market, setMarket] = useState(getCurrentMarket());

    useEffect(() => subscribeToCurrentMarket(setMarket), []);

    // A Dashboard é market-scoped: estratégias/automações/posições/P&L refletem o mercado atual
    // (sem isso, soma futuros no spot — ex.: 1 spot + 1 futuros aparecia como 2 estratégias).
    useEffect(() => {
        getDashboardSummary(market).then(setSummary).catch(() => {});
        getAutomationsCounts(market).then(setAutoCounts).catch(() => {});
    }, [market]);

    useEffect(() => {
        getAiUsage().then(setAiUsage).catch(() => {});
    }, []);

    if (!summary) return null;

    const env = getCurrentEnvironment();
    const positions = summary.openPositions || {};
    const openCount = positions[env] ?? 0;
    const openSub = positions.production !== undefined
        ? `${positions.production} prod · ${positions.test} teste`
        : openCount === 0 ? 'sem ordens pendentes' : 'ordens em aberto';

    const pnlColor = summary.dayPnl > 0 ? 'text-success' : summary.dayPnl < 0 ? 'text-danger' : '';
    // 🚨 Desde 09/08 este card é o RESULTADO REALIZADO das trades que FECHARAM hoje (pareamento
    // FIFO, líquido de taxa quando medida). Antes era `vendas − compras` do dia — fluxo de caixa
    // disfarçado de P&L: num dia que compra mais do que vende, o capital vira ESTOQUE e o card
    // acusava prejuízo do tamanho da diferença. O rótulo tem que dizer o que o número é, senão a
    // troca de semântica passa despercebida.
    const closed = summary.dayClosedTrades;
    const pnlSub = closed === 0
        ? (summary.dayBuyCount > 0
            ? `nenhuma trade fechada hoje · ${summary.dayBuyCount} compra${summary.dayBuyCount > 1 ? 's' : ''} viraram estoque`
            : 'nenhuma trade fechada hoje')
        : `${closed} trade${closed > 1 ? 's' : ''} fechada${closed > 1 ? 's' : ''} hoje${summary.dayPnlIsNet ? ', líquido de taxa' : ' (bruto)'} · % = ROI sobre o custo`;

    function getAiCardContent() {
        if (!aiProvider) return { value: '—', sub: 'não configurada', colorClass: 'text-muted' };
        if (!aiUsage) return { value: '...', sub: aiProvider, colorClass: '' };

        const total = aiUsage.total.inputTokens + aiUsage.total.outputTokens;
        const limit = parseInt(aiMonthlyTokenLimit) || 0;

        let sub = aiProvider;
        let colorClass = '';

        if (limit > 0) {
            const pct = (total / limit) * 100;
            sub = `${total.toLocaleString()} / ${limit.toLocaleString()} tokens`;
            colorClass = pct >= 90 ? 'text-danger' : pct >= 70 ? 'text-warning' : 'text-success';
        } else {
            sub = `${total.toLocaleString()} tokens este mês`;
        }

        return { value: `${aiUsage.total.calls} chamadas`, sub, colorClass };
    }

    const aiCard = getAiCardContent();

    return (
        <div className='row g-3 mb-4'>
            <div className='col-6 col-md-3'>
                <StatCard
                    label='Estratégias ativas'
                    value={summary.activeStrategies}
                    to='/strategies'
                />
            </div>
            <div className='col-6 col-md-3'>
                <StatCard
                    label='Automações ativas'
                    value={summary.activeAutomations}
                    sub={autoCounts
                        ? `${autoCounts.production.active} prod · ${autoCounts.test.active} teste`
                        : null}
                    to='/automations'
                />
            </div>
            <div className='col-6 col-md-3'>
                <StatCard
                    label='Ordens pendentes'
                    value={openCount}
                    sub={openSub}
                    to='/orders'
                />
            </div>
            <div className='col-6 col-md-3'>
                <StatCard
                    label='P&L do dia'
                    value={<>{formatPnl(summary.dayPnl)}{summary.dayPnlPct != null && <span style={{ fontSize: 14 }}> ({pctStr(summary.dayPnlPct)})</span>}</>}
                    sub={pnlSub}
                    colorClass={pnlColor}
                />
            </div>
            <div className='col-6 col-md-3'>
                <StatCard
                    label='IA — mês atual'
                    value={aiCard.value}
                    sub={aiCard.sub}
                    colorClass={aiCard.colorClass}
                    to='/settings'
                />
            </div>
        </div>
    );
}

export default DashboardSummary;
